import React, { useEffect, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import {
  getAuthorPageTextPolicy,
  normalizeAuthorPageVisualUrls,
} from '../../authored/authorPageVisualPolicy';
import { getPageThemeChrome } from '../../reader/pageThemeChrome';
import { editorDirAttr } from '../../authored/rtlEditorAlign';

const DEFAULT_PAGE_TEXTURE = '/images/clear-white-plaster-texture-pattern.jpg';

export default function MobileAuthoredPage({
  page,
  extensions,
  fontScale = 1,
  pageTheme,
  className = '',
  textDir = 'ltr',
}) {
  const visualUrls = useMemo(() => (page ? normalizeAuthorPageVisualUrls(page) : []), [page]);
  const policy = useMemo(
    () =>
      page
        ? getAuthorPageTextPolicy(page, visualUrls)
        : { layoutMode: 'text', allowText: true, maxWords: 0 },
    [page, visualUrls],
  );

  const pageChrome = useMemo(
    () => getPageThemeChrome(pageTheme || { theme_url: DEFAULT_PAGE_TEXTURE }),
    [pageTheme],
  );

  const showEditor =
    page &&
    (policy.layoutMode === 'text' || policy.layoutMode === 'half_one_image') &&
    policy.allowText;

  const editorDir = editorDirAttr(textDir);

  const editor = useEditor(
    {
      immediatelyRender: false,
      editable: false,
      extensions,
      content: { type: 'doc', content: [] },
      editorProps: {
        attributes: {
          class:
            'tiptap-editor max-w-none min-h-0 flex-1 focus:outline-none text-gray-800 leading-relaxed text-shadow font-serif',
          dir: editorDir,
        },
      },
    },
    [extensions],
  );

  useEffect(() => {
    if (!editor) return;
    editor.view.dom.setAttribute('dir', editorDir);
  }, [editor, editorDir]);

  useEffect(() => {
    if (!editor || !page || !showEditor) return;
    const json = page.body_json || { type: 'doc', content: [] };
    editor.commands.setContent(json, false);
  }, [editor, page, showEditor]);

  const bgUrl = pageTheme?.theme_url || DEFAULT_PAGE_TEXTURE;
  const textStyle = { fontSize: `${16 * fontScale}px` };

  const sheetStyle = {
    backgroundImage: `url(${bgUrl})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundColor: pageChrome?.surfaceFallback ?? '#f5f5f4',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.35), 0 2px 8px rgba(0, 0, 0, 0.2)',
    borderLeft: `4px solid ${pageChrome?.accentColor ?? '#d1d5db'}`,
  };

  if (!page) {
    return (
      <article
        className={`relative flex min-h-[calc(100dvh-12.5rem)] w-full flex-col items-center justify-center font-serif text-gray-400 ${className}`}
        style={sheetStyle}
      >
        Empty page
      </article>
    );
  }

  return (
    <article
      className={`relative flex min-h-[calc(100dvh-12.5rem)] w-full flex-col overflow-hidden ${className}`}
      style={sheetStyle}
    >
      <div
        className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 py-7 pb-12 font-serif text-gray-800 text-shadow"
        style={textStyle}
      >
        {page.chapter_heading ? (
          <h2 className="mb-2 shrink-0 text-2xl font-bold text-gray-900">{page.chapter_heading}</h2>
        ) : null}
        {page.chapter_subheading ? (
          <h3 className="mb-4 shrink-0 text-lg text-gray-600">{page.chapter_subheading}</h3>
        ) : null}

        {policy.layoutMode === 'half_one_image' && visualUrls[0] ? (
          <div className="flex min-h-0 flex-1 flex-col gap-3">
            <div className="flex min-h-[9rem] shrink-0 basis-[52%] flex-col overflow-hidden rounded-md border border-stone-300/80 bg-stone-100/90 p-2 shadow-inner">
              <div className="relative min-h-0 flex-1 overflow-hidden">
                <img
                  src={visualUrls[0]}
                  alt=""
                  className="absolute inset-0 h-full w-full object-contain"
                />
              </div>
            </div>
            {showEditor && editor ? (
              <div className="min-h-0 flex-1 overflow-y-auto border-t border-stone-300/60 pt-3">
                <EditorContent editor={editor} />
              </div>
            ) : null}
          </div>
        ) : null}

        {policy.layoutMode === 'full_bleed' && visualUrls[0] ? (
          <div className="-mx-2 flex min-h-0 flex-1 flex-col">
            <div className="relative min-h-0 flex-1 overflow-hidden rounded-sm bg-stone-100/80">
              <img
                src={visualUrls[0]}
                alt=""
                className="absolute inset-0 h-full w-full object-contain object-center"
              />
            </div>
          </div>
        ) : null}

        {policy.layoutMode === 'half_two_images' ? (
          <div className="flex min-h-0 flex-1 flex-col gap-3">
            {[0, 1].map((slot) => (
              <div
                key={visualUrls[slot] ?? `slot-${slot}`}
                className="relative min-h-[8rem] flex-1 overflow-hidden rounded-md border border-stone-300/80 bg-stone-100/90 shadow-inner"
              >
                {visualUrls[slot] ? (
                  <img
                    src={visualUrls[slot]}
                    alt=""
                    className="absolute inset-0 h-full w-full object-contain"
                  />
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {policy.layoutMode === 'text' && showEditor && editor ? (
          <div className="min-h-0 flex-1">
            <EditorContent editor={editor} />
          </div>
        ) : null}
      </div>

      {page.page_order_no != null ? (
        <div className="pointer-events-none absolute bottom-5 left-0 right-0 text-center text-base font-serif leading-relaxed text-gray-800 text-shadow">
          {page.page_order_no}
        </div>
      ) : null}
    </article>
  );
}
